import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Card, CardHeader, CardContent } from './ui/Card';
import { fetchStats } from '../api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/* Цвета графика читаются из CSS-переменных темы — график всегда в тон UI. */
const readToken = (name) => {
  if (typeof window === 'undefined') return undefined;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || undefined;
};

const withAlpha = (color, alphaHex) =>
  color && /^#[0-9a-fA-F]{6}$/.test(color) ? `${color}${alphaHex}` : color;

const KpiTile = ({ icon, value, label, valueClass = 'text-text-strong', title }) => (
  <div className="rounded-md border border-border bg-surface-sunken p-lg text-center">
    <i className={`bi ${icon} text-body-md text-primary`} aria-hidden="true"></i>
    <div className={`tabular text-kpi mt-xs truncate ${valueClass}`} title={title}>
      {value}
    </div>
    <p className="eyebrow mt-xs">{label}</p>
  </div>
);

const Stats = ({ totalLinks }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Перерисовка графика при смене темы: тема меняет data-bs-theme на <html>.
  const [themeKey, setThemeKey] = useState(0);

  useEffect(() => {
    const observer = new MutationObserver(() => setThemeKey((k) => k + 1));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });
    return () => observer.disconnect();
  }, []);

  // Single effect: reload only when totalLinks changes; stale responses are ignored.
  useEffect(() => {
    if (totalLinks === 0) {
      setStats({ total_links: 0, genres: {} });
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const data = await fetchStats();
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled) setStats({ total_links: 0, genres: {} });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [totalLinks]);

  if (loading) {
    return (
      <Card className="mb-3xl animate-fade-in">
        <CardHeader title="Статистика по жанрам" icon="bi-bar-chart" />
        <CardContent>
          <div className="grid grid-cols-1 gap-md sm:grid-cols-3 mb-xl">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-md border border-border bg-surface-sunken p-lg text-center">
                <div className="skeleton mx-auto h-7 w-14" />
                <div className="skeleton mx-auto mt-md h-2 w-16" />
              </div>
            ))}
          </div>
          <div className="skeleton h-[160px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!stats || stats.total_links === 0) {
    return (
      <Card className="mb-3xl animate-fade-in">
        <CardHeader title="Статистика по жанрам" icon="bi-bar-chart" />
        <CardContent className="py-2xl text-center">
          <i className="bi bi-graph-up text-kpi text-text-muted" aria-hidden="true"></i>
          <p className="mt-md text-body-sm text-text-muted">
            Статистика появится после завершения анализа
          </p>
        </CardContent>
      </Card>
    );
  }

  const entries = Object.entries(stats.genres || {}).sort(([, a], [, b]) => b - a);
  const topGenre = entries[0]?.[0] || '—';

  const primary = readToken('--color-primary');
  const textColor = readToken('--color-text');
  const textStrong = readToken('--color-text-strong');
  const textMuted = readToken('--color-text-muted');
  const border = readToken('--color-border');
  const borderStrong = readToken('--color-border-strong');
  const surfaceRaised = readToken('--color-surface-raised');

  const chartData = {
    labels: entries.map(([g]) => g),
    datasets: [
      {
        label: 'Количество',
        data: entries.map(([, c]) => c),
        backgroundColor: withAlpha(primary, '99'),
        borderColor: primary,
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: surfaceRaised,
        titleColor: textStrong,
        bodyColor: textColor,
        borderColor: borderStrong,
        borderWidth: 1,
        cornerRadius: 6,
        titleFont: { family: "'Fira Code', monospace", size: 10 },
        bodyFont: { family: "'Fira Sans', sans-serif", size: 12 },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: withAlpha(border, '80') },
        ticks: {
          color: textMuted,
          precision: 0,
          font: { family: "'Fira Code', monospace", size: 10 },
        },
      },
      y: {
        grid: { display: false },
        ticks: {
          color: textColor,
          font: { family: "'Fira Sans', sans-serif", size: 12 },
        },
      },
    },
  };

  return (
    <Card className="mb-3xl animate-fade-in">
      <CardHeader title="Статистика по жанрам" icon="bi-bar-chart" />
      <CardContent>
        <div className="mb-xl grid grid-cols-1 gap-md sm:grid-cols-3">
          <KpiTile icon="bi-link-45deg" value={stats.total_links} label="Всего ссылок" />
          <KpiTile icon="bi-music-note-list" value={entries.length} label="Жанров" />
          <KpiTile
            icon="bi-trophy"
            value={topGenre}
            label="Топ жанр"
            valueClass="text-accent text-section-title leading-[32px]"
            title={topGenre}
          />
        </div>
        {/* key={themeKey} пересоздаёт график при смене темы — цвета
            перечитываются из CSS-переменных. */}
        <div key={themeKey} style={{ height: Math.max(160, entries.length * 32) }}>
          <Bar
            data={chartData}
            options={chartOptions}
            aria-label="Распределение ссылок по жанрам"
            role="img"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default Stats;
